import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { DisplayTable } from '@/components/tables/DisplayTable/DisplayTable';
import { TableRowSkeleton } from '@/components/tables/TableRowSkeleton/TableRowSkeleton';

import { EntityLinkFormShape, EntityLinkRow } from './EntityLinkTable.types';
import { EntityLinkTableRow } from './EntityLinkTableRow';

export interface EntityLinkTableProps {
  rows: EntityLinkRow[];
  householdId: string;
  /** Provider display name, e.g. "Addepar" — used in the second column header. */
  integrationProviderName: string;
  /** Header for the left column. */
  entityColumnHeader?: string;
  loading: boolean;
}

const SKELETON_ROW_COUNT = 6;

/**
 * The two-column table both "Link data" panes render: the configured entity
 * label on the left, and the integration data attached to it on the right.
 */
export function EntityLinkTable({
  rows,
  householdId,
  integrationProviderName,
  entityColumnHeader = 'Luminary entity',
  loading,
}: EntityLinkTableProps) {
  const { control } = useFormContext<EntityLinkFormShape>();

  const allEntities = useWatch({ control, name: 'entities' });

  // Which integration entities are spoken for, so no two rows can claim the
  // same one.
  const selectedIntegrationEntityIds = useMemo(() => {
    const ids = new Set<string>();
    Object.values(allEntities ?? {}).forEach((entity) => {
      entity?.integrationEntities?.forEach((id) => ids.add(id));
    });
    return ids;
  }, [allEntities]);

  const columns = useMemo(
    () => [
      { headerName: entityColumnHeader, width: '50%' },
      {
        headerName: `${integrationProviderName} data link`,
        width: '50%',
      },
    ],
    [entityColumnHeader, integrationProviderName]
  );

  return (
    <DisplayTable columns={columns} tableSx={{ tableLayout: 'fixed' }}>
      {loading
        ? Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <TableRowSkeleton key={index} columns={columns.length} />
          ))
        : rows.map((row) => (
            <EntityLinkTableRow
              key={row.id}
              row={row}
              householdId={householdId}
              selectedIntegrationEntityIds={selectedIntegrationEntityIds}
            />
          ))}
    </DisplayTable>
  );
}
